<template>
  <div
    class="dashboard-space p-5 w-100"
  >
    <global-styles-render />

    <!-- Modal -->
    <b-container fluid>
      <b-row class="mb-5">
        <b-col md="3">
          <h4 class="font-weight-bold mb-0">
            Modal Builder
          </h4>
        </b-col>

        <b-col
          v-if="checkPermissions('modal.create')"
          class="text-right"
        >
          <b-btn
            v-b-modal.import-modal
            variant="secondary"
            class="mr-4"
          >
            <div class="d-flex align-items-center font-weight-bold">
              <div class="mr-2">
                Import Modal
              </div>

              <svg
                width="19"
                height="20"
                viewBox="0 0 19 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.75 13.9583C4.75 14.6946 4.75 15.0627 4.83093 15.3647C5.05054 16.1843 5.69071 16.8245 6.51031 17.0441C6.81233 17.125 7.18044 17.125 7.91667 17.125H12.825C14.1551 17.125 14.8202 17.125 15.3282 16.8661C15.7751 16.6384 16.1384 16.2751 16.3661 15.8282C16.625 15.3202 16.625 14.6551 16.625 13.325V6.675C16.625 5.34488 16.625 4.67981 16.3661 4.17177C16.1384 3.72489 15.7751 3.36156 15.3282 3.13386C14.8202 2.875 14.1551 2.875 12.825 2.875H7.91667C7.18044 2.875 6.81232 2.875 6.5103 2.95593C5.69071 3.17554 5.05053 3.81571 4.83093 4.63531C4.75 4.93733 4.75 5.30544 4.75 6.04167M9.5 6.83333L12.6667 10M12.6667 10L9.5 13.1667M12.6667 10H2.375"
                  stroke="#6E747A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </b-btn>

          <b-btn
            v-b-modal.new-modal
            variant="primary"
          >
            <div class="d-flex align-items-center font-weight-bold">
              <div class="mr-2">
                Create New Modal
              </div>
              <svg
                fill="none"
                height="18"
                viewBox="0 0 18 18"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M17.6 9.3C17.6 13.884 13.884 17.6 9.3 17.6C4.71604 17.6 1 13.884 1 9.3C1 4.71604 4.71604 1 9.3 1C13.884 1 17.6 4.71604 17.6 9.3ZM9.3 16.3C13.166 16.3 16.3 13.166 16.3 9.3C16.3 5.43401 13.166 2.3 9.3 2.3C5.43401 2.3 2.3 5.43401 2.3 9.3C2.3 13.166 5.43401 16.3 9.3 16.3Z"
                  fill="currentColor"
                  fill-rule="evenodd"
                />
                <path
                  d="M12.9 9.53198V8.26798H10.036V5.14798H8.64398V8.26798H5.76398V9.53198H8.64398V12.78H10.036V9.53198H12.9Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </b-btn>
        </b-col>
      </b-row>

      <div class="py-5">
        <div v-if="!loading">
          <div v-if="checkPermissions('modal.read') && modal.total !== 0">
            <b-list-group>
              <es-modal-list-item
                v-for="modal in modal.data"
                :key="modal.id"
                :modal="modal"
                @deleted="fetchModals"
                @duplicated="fetchModals"
              />
            </b-list-group>

            <b-pagination
              v-if="modal.total > modal.per_page"
              v-model="currentPage"
              :per-page="modal.per_page"
              :total-rows="modal.total"
            />
          </div>
          <div v-else>
            <p class="text-muted text-center">
              You have not created any modals yet
            </p>
          </div>
        </div>
        <div
          v-else
          class="d-flex align-items-center justify-content-center py-5"
        >
          <b-spinner variant="primary" />
        </div>
      </div>
    </b-container>
    <!-- End Modal -->

    <import-modal @modal-imported="fetchModals" />
    <new-modal />

    <custom-modal />
  </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import OveralSettingsMixin from '../../components/mixins/OveralSettingsMixin'
import RouterMixin from '../../components/mixins/RouterMixin'
import NewModal from './components/NewModal'
import ImportModal from './components/ImportModal.vue'
import EsModalListItem from './components/EsModalListItem'
import CustomModal from '../../components/editor/modals/CustomModal'
import GlobalStylesRender from '@/components/builder/utils/GlobalStylesRender'

export default {
  name: 'EsModal',
  components: {
    GlobalStylesRender,
    CustomModal,
    EsModalListItem,
    NewModal,
    ImportModal
  },
  filters: {
    strippedContent: function (string) {
      return string.replace(/<\/?[^>]+>/ig, ' ')
    }
  },
  mixins: [OveralSettingsMixin, RouterMixin],
  data () {
    return {
      search: '',
      loading: true,
      modal: {},
      currentPage: 1
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    })
  },
  watch: {
    currentPage () {
      this.fetchModals()
    }
  },
  methods: {
    async fetchModals () {
      this.loading = true

      await axios.get(`api/projects/${this.project.id}/modals?page=${this.currentPage}&q=${this.search}`)
        .then(({data}) => {
          this.modal = data.data
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  async mounted () {
    await this.$store.dispatch('projects/hasAccessToPage', 'modal')
    await this.fetchModals()
    document.body.classList.add('es-modal')
  },
  beforeDestroy () {
    document.body.classList.remove('es-modal')
  }
}
</script>

<style lang="scss">
.es-modal .custom-modal {
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 10000000;
  }
}
</style>
